import { Drawer } from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { useUpdateIndirectCosts } from '@/features/project/projects/detail/payment/_hooks/payment';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { IndirectCostsForm } from '../../fund/_components/IndirectCostsForm';
import { PaymentContractFormDrawer } from './PaymentContractFormDrawer';
import { PaymentEdgeDrawer } from './PaymentEdgeDrawer';
import { PaymentTabItems } from './PaymentTabItems';

type Props = Pick<z.infer<typeof ProjectModel>, 'paymentIndirectCosts'>;

export const PaymentTabContents: FC<Props> = ({ paymentIndirectCosts }) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const project = useProject(projectId);

  const { drawerState, setDrawerState } = useProjectDetailDrawerState();

  const { mutateAsync: updateIndirectCostsAsync } = useUpdateIndirectCosts();

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  if (!project.data) {
    return null;
  }

  return (
    <>
      <PaymentTabItems />

      <Drawer
        open={drawerState.open && drawerState.type === 'indirect-costs'}
        onClose={handleClose}
        destroyOnClose
      >
        <IndirectCostsForm
          sheetType={project.data.sheetType}
          initialValues={{
            paymentIndirectCosts,
          }}
          onSubmit={async (values) => {
            await updateIndirectCostsAsync({ ...values, projectId });

            await Promise.all([
              openMessage('success', t('message-update-completed')),
              handleClose(),
            ]);
          }}
        />
      </Drawer>

      <PaymentContractFormDrawer />
      <PaymentEdgeDrawer />
    </>
  );
};
